/* eslint-disable max-len */
/* eslint-disable max-lines */
/* eslint-disable id-length */
/* eslint-disable max-lines-per-function */
import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { TableUtility } from '@utility/dynamic-table';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import { useAuth } from '../../Hooks/useAuth';
import { getExportFileName, getExportFileNameWithDate } from '../Delivery/utils';
import UseAxios from '../../Api/http/http';
import './poDetails.scss';
import Loader from '../../Components/Loader';
import PODetailsModal from './PODetailsModal';
import BulkStatusModal from './BulkStatusModal';
import {
  appendServiceNameToURL,
  appendServicePortToURL,
} from '../../Api/resources/serviceURLs';
import { serviceName } from '../../Api/resources/serviceConstants';
import { PO_DETAILS_LIST } from '../../Api/resources/endPoint';
import {
  getPODetailsById,
  createPODetails,
  updatePODetails,
  bulkUpdatePOStatusRequest,
  deletePODetailsRequest,
  getClientsListRequest,
} from '../../Api/request/poDetails';

// Fetch base URL dynamically
const fetchBaseURL = async () => {
  const serviceURL = await appendServiceNameToURL(serviceName.MASTER_MS);
  const servicePort = await appendServicePortToURL(serviceName.MASTER_MS);
  if (process.env.NODE_ENV === 'production') {
    return process.env.REACT_APP_API_URL + serviceURL + '/api' + PO_DETAILS_LIST;
  } else {
    return process.env.REACT_APP_API_URL
      + `:${servicePort}`
      + serviceURL
      + '/api'
      + PO_DETAILS_LIST;
  }
};

const PO_DETAILS_MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const formatAmountIN = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) return String(value);
  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numericValue);
};

const formatDateDDMMMYYYY = (input) => {
  if (input === null || input === undefined || input === '') return '-';

  const parsedDate = new Date(input);
  if (Number.isNaN(parsedDate.getTime())) return String(input);

  const day = String(parsedDate.getDate()).padStart(2, '0');
  const month = PO_DETAILS_MONTHS[parsedDate.getMonth()];
  const year = parsedDate.getFullYear();

  return `${day}-${month}-${year}`;
};

const formatPoDisplayValue = (fieldName, value) => {
  if (value === null || value === undefined || value === '') return value;

  const normalizedFieldName = String(fieldName || '').toLowerCase();

  if (normalizedFieldName.includes('amount')) {
    return formatAmountIN(value);
  }

  if (normalizedFieldName.includes('date')) {
    return formatDateDDMMMYYYY(value);
  }

  return value;
};

const getClientNameFromRow = (rowData = {}) => {
  const nameCandidates = [
    rowData.client_name,
    rowData.clientName,
    rowData.client_name_text,
    rowData.zoho_client_name,
    rowData.client,
    rowData.client_label,
    rowData.clientLabel,
  ];

  const resolvedName = nameCandidates.find((value) => (
    typeof value === 'string' && value.trim()
  ));

  return resolvedName ? resolvedName.trim() : '';
};

const getClientIdFromRow = (rowData = {}) => {
  const idCandidates = [
    rowData.client_id,
    rowData.clientId,
    rowData.zoho_client_id,
    rowData.client_value,
    rowData.clientValue,
  ];

  const resolvedId = idCandidates.find((value) => value !== null && value !== undefined && String(value).trim());
  return resolvedId !== undefined ? String(resolvedId).trim() : '';
};

const buildClientIdNameMap = (rows = []) => {
  const clientMap = new Map();

  (Array.isArray(rows) ? rows : []).forEach((rowData) => {
    const clientId = getClientIdFromRow(rowData || {});
    const clientName = getClientNameFromRow(rowData || {});

    if (clientId && clientName) {
      clientMap.set(clientId, clientName);
    }
  });

  return clientMap;
};

const mergeClientMapFromMasterList = (clientMap, clients = []) => {
  const nextMap = new Map(clientMap || []);

  (Array.isArray(clients) ? clients : []).forEach((client) => {
    const clientId = client?.id !== undefined && client?.id !== null ? String(client.id).trim() : '';
    const clientName = String(client?.clientName || '').trim();

    if (clientId && clientName) {
      nextMap.set(clientId, clientName);
    }
  });

  return nextMap;
};

const CLIENT_OPTION_ARRAY_KEYS = [
  'options',
  'values',
  'items',
  'data',
  'list',
  'filterOptions',
  'filterValues',
];

const normalizeClientFilterOption = (option, clientIdNameMap) => {
  if (!clientIdNameMap || clientIdNameMap.size === 0) return option;

  if (typeof option === 'number' || typeof option === 'string') {
    const candidateId = String(option).trim();
    return clientIdNameMap.get(candidateId) || option;
  }

  if (!option || typeof option !== 'object') {
    return option;
  }

  const normalizedOption = { ...option };
  const idCandidates = [
    normalizedOption.value,
    normalizedOption.id,
    normalizedOption.key,
    normalizedOption.client_id,
    normalizedOption.clientId,
  ];
  const rawId = idCandidates.find((value) => value !== null && value !== undefined && String(value).trim());
  const candidateId = rawId !== undefined ? String(rawId).trim() : '';
  const resolvedName = candidateId ? clientIdNameMap.get(candidateId) : '';

  if (resolvedName) {
    if ('label' in normalizedOption) normalizedOption.label = resolvedName;
    if ('name' in normalizedOption) normalizedOption.name = resolvedName;
    if ('text' in normalizedOption) normalizedOption.text = resolvedName;
    if ('displayValue' in normalizedOption) normalizedOption.displayValue = resolvedName;
    if (!('label' in normalizedOption) && !('name' in normalizedOption) && !('text' in normalizedOption)) {
      normalizedOption.label = resolvedName;
    }
  }

  CLIENT_OPTION_ARRAY_KEYS.forEach((keyName) => {
    if (Array.isArray(normalizedOption[keyName])) {
      normalizedOption[keyName] = normalizedOption[keyName]
        .map((item) => normalizeClientFilterOption(item, clientIdNameMap));
    }
  });

  return normalizedOption;
};

const isClientFilterConfig = (filterConfig = {}) => {
  if (!filterConfig || typeof filterConfig !== 'object') return false;

  const keysToCheck = [
    filterConfig.field,
    filterConfig.label,
    filterConfig.name,
    filterConfig.title,
    filterConfig.header,
    filterConfig.filterName,
    filterConfig.displayName,
  ];

  return keysToCheck.some((value) => (
    String(value || '')
      .toLowerCase()
      .includes('client')
  ));
};

const normalizeClientFilters = (filters = [], rows = [], clients = []) => {
  if (!Array.isArray(filters) || filters.length === 0) return filters;

  const clientIdNameMap = mergeClientMapFromMasterList(buildClientIdNameMap(rows), clients);
  const uniqueClientNames = Array.from(new Set(
    (Array.isArray(rows) ? rows : [])
      .map((rowData) => getClientNameFromRow(rowData || {}))
      .filter((name) => typeof name === 'string' && name.trim()),
  ));

  const injectClientNamesIntoFilter = (filterConfig = {}) => {
    const nextFilterConfig = { ...filterConfig };
    let hasOptionArray = false;

    CLIENT_OPTION_ARRAY_KEYS.forEach((keyName) => {
      if (Array.isArray(nextFilterConfig[keyName])) {
        hasOptionArray = true;
        nextFilterConfig[keyName] = uniqueClientNames;
      }
    });

    if (!hasOptionArray) {
      nextFilterConfig.values = uniqueClientNames;
    }

    return nextFilterConfig;
  };

  if (clientIdNameMap.size === 0 && uniqueClientNames.length === 0) return filters;

  return filters.map((filterConfig) => {
    if (!isClientFilterConfig(filterConfig)) return filterConfig;

    if (clientIdNameMap.size === 0 && uniqueClientNames.length > 0) {
      return injectClientNamesIntoFilter(filterConfig);
    }

    return normalizeClientFilterOption(filterConfig, clientIdNameMap);
  });
};

const normalizeClientFilterMenuItems = (
  menuItems = [],
  columnConfig = {},
  clientIdNameMap = new Map(),
  uniqueClientNames = [],
) => {
  const hasClientMap = clientIdNameMap instanceof Map && clientIdNameMap.size > 0;
  const hasClientNames = Array.isArray(uniqueClientNames) && uniqueClientNames.length > 0;
  if (!Array.isArray(menuItems) || (!hasClientMap && !hasClientNames)) return menuItems;

  const mapClientListValues = (values = []) => {
    if (!Array.isArray(values)) return values;

    if (values.every((value) => value && typeof value === 'object')) {
      return values.map((value) => normalizeClientFilterOption(value, clientIdNameMap));
    }

    if (hasClientMap) {
      return values.map((value) => {
        const keyValue = String(value ?? '').trim();
        return clientIdNameMap.get(keyValue) || value;
      });
    }

    return hasClientNames ? uniqueClientNames : values;
  };

  return menuItems.map((menuItem = {}) => {
    const menuLooksLikeClient = isClientFilterConfig({
      ...columnConfig,
      ...menuItem,
      header: columnConfig?.header,
      field: columnConfig?.field,
    });

    if (!Array.isArray(menuItem.items)) return menuItem;

    const normalizedItems = menuItem.items.map((item = {}) => {
      const itemLooksLikeClient = menuLooksLikeClient || isClientFilterConfig(item);
      if (!itemLooksLikeClient || item?.type !== 'filter') return item;

      const nextItem = { ...item };
      // Ensure this menu always filters by the actual client name field.
      nextItem.field = 'client_name';
      nextItem.label = nextItem.label || 'Filter by Client Name';

      CLIENT_OPTION_ARRAY_KEYS.forEach((keyName) => {
        if (Array.isArray(nextItem[keyName])) {
          nextItem[keyName] = mapClientListValues(nextItem[keyName]);
        }
      });

      if (hasClientNames) {
        nextItem.selectedValues = [];
      }

      if (hasClientNames) {
        nextItem.values = uniqueClientNames;
        nextItem.options = uniqueClientNames;
      }

      return nextItem;
    });

    return {
      ...menuItem,
      items: normalizedItems,
    };
  });
};

const normalizeAppliedFilterPayload = (payload = {}, rows = [], clients = []) => {
  if (!payload || typeof payload !== 'object') return payload;

  const clientIdNameMap = mergeClientMapFromMasterList(buildClientIdNameMap(rows), clients);
  const legacyClientValues = payload.client_name ?? payload.client_id;

  if (legacyClientValues === undefined || legacyClientValues === null) return payload;

  const clientNames = (Array.isArray(legacyClientValues) ? legacyClientValues : [legacyClientValues])
    .map((value) => {
      const optionValue = value && typeof value === 'object' && value.value !== undefined
        ? value.value
        : value;
      const mappedName = clientIdNameMap.get(String(optionValue ?? '').trim());
      return mappedName || optionValue;
    })
    .filter((value) => value !== null && value !== undefined && String(value).trim() !== '');

  const normalizedPayload = { ...payload, client_name: clientNames };
  delete normalizedPayload.client_id;
  return normalizedPayload;
};


const mergeAndDedupeFieldOptions = (menuItems = [], fieldName = '') => {
  if (!Array.isArray(menuItems) || !fieldName) return menuItems;

  // Collect first-seen option objects keyed by normalized value
  const seen = new Map();

  for (const menuItem of menuItems) {
    if (!Array.isArray(menuItem.items)) continue;
    for (const item of menuItem.items) {
      if (item?.type !== 'filter' || String(item.field || '') !== fieldName || !Array.isArray(item.options)) continue;
      for (const opt of item.options) {
        const key = String(opt?.value ?? opt?.label ?? '').trim()
          .toLowerCase();
        if (!key) continue;
        if (!seen.has(key)) seen.set(key, opt);
      }
    }
  }

  if (seen.size === 0) return menuItems;

  const dedupedOptions = Array.from(seen.values());

  // Apply deduped options back to every matching filter item so UI shows a single set
  return menuItems.map((menuItem = {}) => {
    if (!Array.isArray(menuItem.items)) return menuItem;
    const nextItems = menuItem.items.map((item = {}) => {
      if (item?.type !== 'filter' || String(item.field || '') !== fieldName) return item;
      return {
        ...item,
        options: dedupedOptions,
      };
    });
    return {
      ...menuItem,
      items: nextItems,
    };
  });
};

const PO_DETAILS_HEADER_LABELS = {
  serialNumber: 'SI no.',
  srNo: 'SI no.',
  received_date: 'PO Received Date',
  start_date: 'PO Start Date',
  expire_date: 'PO Expire Date',
  client_name: 'Zoho Client Name',
  amount: 'Amount (INR)',
  credit_period: 'Credit Period (Days)',
};

const PO_DETAILS_COLUMN_ORDER = new Map([
  ['__rowSelect', 0],
  ['serialNumber', 10],
  ['srNo', 10],
  ['po_number', 20],
  ['client_name', 30],
  ['amount', 40],
  ['start_date', 50],
  ['received_date', 60],
  ['expire_date', 70],
  ['po_status', 80],
  ['credit_period', 90],
  ['po_links', 100],
  ['action', 110],
]);

const getPoDetailsColumnField = (column = {}) => String(column?.field ?? column?.key ?? '').trim();

const getPoDetailsColumnOrder = (column = {}) => {
  const fieldName = getPoDetailsColumnField(column);

  if (PO_DETAILS_COLUMN_ORDER.has(fieldName)) {
    return PO_DETAILS_COLUMN_ORDER.get(fieldName);
  }

  const numericOrder = Number(column?.order);
  return Number.isFinite(numericOrder) ? numericOrder : 50;
};

const getActionsColumnDefinition = () => ({
  field: 'action',
  header: 'Action',
  label: 'Action',
  className: 'po-details-actions-column',
  headerClassName: 'po-details-actions-column',
  bodyClassName: 'po-details-actions-column',
  visible: true,
  order: 999,
  width: '72px',
  minWidth: '72px',
  maxWidth: '72px',
  align: 'center',
  defaultPinned: false,
  pinnable: false,
  sortable: false,
  filter: false,
  filterVisible: false,
  showFilterButtonInHeader: false,
  showSortButtonInHeader: false,
  showPinButtonInHeader: false,
  isGlobalFilter: false,
  menuItems: [],
});

const resolvePoId = (rowOrId) => {
  if (rowOrId && typeof rowOrId === 'object') {
    return rowOrId.po_id || rowOrId.id || rowOrId.poId || rowOrId.poID || null;
  }

  if (typeof rowOrId === 'string') {
    const trimmedValue = rowOrId.trim();
    return trimmedValue || null;
  }

  return rowOrId || null;
};

const normalizePoLinks = (rowData = {}) => {
  const parsedLinks = [];

  if (rowData.po_links_raw) {
    parsedLinks.push(rowData.po_links_raw);
  }

  if (Array.isArray(rowData.po_links)) {
    parsedLinks.push(...rowData.po_links);
  } else if (typeof rowData.po_links === 'string' || typeof rowData.po_links === 'number') {
    parsedLinks.push(...String(rowData.po_links).split(/[\s,;|]+/));
  }

  if (typeof rowData.po_link === 'string' || typeof rowData.po_link === 'number') {
    parsedLinks.push(...String(rowData.po_link).split(/[\s,;|]+/));
  }

  return parsedLinks
    .map((linkValue) => (
      typeof linkValue === 'string' || typeof linkValue === 'number'
        ? String(linkValue).trim()
        : ''
    ))
    .filter(Boolean);
};

const normalizePoRow = (rowData = {}) => ({
  ...rowData,
  po_links: normalizePoLinks(rowData),
});

const toNavigableUrl = (linkValue) => {
  const normalizedLinkValue = String(linkValue || '').trim();

  if (!normalizedLinkValue) return null;

  const linkWithProtocol = /^https?:\/\//i.test(normalizedLinkValue)
    ? normalizedLinkValue
    : `https://${normalizedLinkValue}`;

  try {
    const parsedLink = new URL(linkWithProtocol);
    if (!['http:', 'https:'].includes(parsedLink.protocol)) return null;
    return parsedLink.href;
  } catch (error) {
    return null;
  }
};

const getPrimaryPoLinkUrl = (rowData = {}) => {
  const normalizedLinks = normalizePoLinks(rowData);

  for (const linkValue of normalizedLinks) {
    const navigableLink = toNavigableUrl(linkValue);
    if (navigableLink) return navigableLink;
  }

  return null;
};

const getTableApiCandidates = (tableUtilityRef, tableStateRef) => {
  const candidates = [];

  if (tableStateRef?.current && typeof tableStateRef.current === 'object') {
    candidates.push(tableStateRef.current);
  }

  if (tableUtilityRef?.current && typeof tableUtilityRef.current === 'object') {
    candidates.push(tableUtilityRef.current);
  }

  return candidates;
};

const refreshTableUtility = (tableUtilityRef, tableStateRef) => {
  const apiCandidates = getTableApiCandidates(tableUtilityRef, tableStateRef);

  if (apiCandidates.length === 0) return false;

  for (const tableApi of apiCandidates) {
    if (typeof tableApi.refreshTableData === 'function') {
      tableApi.refreshTableData();
      return true;
    }

    if (typeof tableApi.refreshTable === 'function') {
      tableApi.refreshTable();
      return true;
    }

    if (typeof tableApi.fetchTableData === 'function') {
      tableApi.fetchTableData();
      return true;
    }
  }

  return false;
};

const PoDetails = () => {
  const { userProfile } = useAuth();
  const [baseURL, setBaseURL] = useState('');
  const [clients, setClients] = useState([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingPoId, setEditingPoId] = useState(null);
  const [formData, setFormData] = useState({});
  const [__selectedPO, set__selectedPO] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [, setGlobalFilters] = useState([]);
  const [__appliedFilterPayload, setAppliedFilterPayload] = useState({});
  const [deletedPoIds, setDeletedPoIds] = useState([]);
  const [tableResetKey, setTableResetKey] = useState(0);
  const tableRowsRef = useRef([]);
  const selectedRowsRef = useRef([]);
  const applySelectionToTableRef = useRef(null);
  const tableUtilityRef = useRef(null);
  const tableStateRef = useRef(null);
  // Keep tableConfig reference stable so TableUtility doesn't re-run its
  // initialization fetch on every parent re-render.
  const stableTableConfig = useMemo(() => [], []);

  const poStatuses = ['In Progress', 'On Hold', 'Closed', 'Dropped'];

  // Get PO Details permissions using standard pattern.
  const poPermissions = userProfile?.permissions?.['finance:po-details'] || [];

  // Helper function to check if user has specific permission
  const hasAccess = useCallback((action) => {
    return poPermissions.includes(action);
  }, [poPermissions]);

  // Permission flags for different actions
  const canCreate = hasAccess('create');
  const canUpdate = hasAccess('update');
  const canDelete = hasAccess('delete');

  // Show selection column ONLY when user has update permission (needed for bulk status change)
  // View-only users should NOT see row selection checkboxes
  const showSelectionColumn = canUpdate;

  const selectionHeading = (
    <div className="po-details-toolbar-heading">
      <span className="po-details-toolbar-title">PO Details</span>
      <div className="po-details-toolbar-selection-area">
        <span className="po-details-toolbar-selection-count">
          {`No.of POs Selected : ${selectedRows.length}`}
        </span>
        {canUpdate && (
          <button
            type="button"
            className="po-details-toolbar-status-btn"
            onClick={() => {
              if (selectedRows.length === 0) {
                toast.error('Please select at least one PO to update status');
                return;
              }

              setShowBulkModal(true);
            }}
          >
            Update Status
          </button>
        )}
      </div>
    </div>
  );

  const titleHeading = (
    <div className="po-details-toolbar-heading">
      <span className="po-details-toolbar-title">PO Details</span>
    </div>
  );

  useEffect(() => {
    selectedRowsRef.current = selectedRows;
  }, [selectedRows]);

  const selectionColumnField = '__rowSelect';
  const useManualSelection = true;

  const getRowSelectionId = useCallback((rowData) => resolvePoId(rowData), []);

  const getSelectedRowIds = useCallback((rows = []) => {
    const ids = rows
      .map((row) => getRowSelectionId(row))
      .filter(Boolean);

    return new Set(ids);
  }, [getRowSelectionId]);


  const buildSelectionColumn = useCallback((rows = [], selectedIds = new Set()) => {
    const rowIds = rows
      .map((row) => getRowSelectionId(row))
      .filter(Boolean);
    const allSelected = rowIds.length > 0 && rowIds.every((id) => selectedIds.has(id));

    return {
      field: selectionColumnField,
      header: (
        <input
          type="checkbox"
          className="po-details-select-all"
          aria-label="Select all rows"
          defaultChecked={allSelected}
          onChange={(event) => {
            const currentRows = Array.isArray(tableRowsRef.current)
              ? tableRowsRef.current
              : [];
            const nextSelectedRows = event.target.checked ? currentRows : [];

            selectedRowsRef.current = nextSelectedRows;
            setSelectedRows(nextSelectedRows);
            if (applySelectionToTableRef.current) {
              applySelectionToTableRef.current(currentRows, nextSelectedRows);
            }
          }}
        />
      ),
      filter: false,
      sortable: false,
      pinnable: false,
      defaultPinned: true,
      isPinned: true,
      visible: true,
      showFilterButtonInHeader: false,
      showPinButtonInHeader: false,
      menuItems: [],
      style: { width: '36px' },
    };
  }, [getRowSelectionId]);

  // If the user loses update permission (view-only), clear any selected rows immediately
  useEffect(() => {
    if (!showSelectionColumn && Array.isArray(selectedRows) && selectedRows.length > 0) {
      selectedRowsRef.current = [];
      setSelectedRows([]);
      if (applySelectionToTableRef.current) {
        applySelectionToTableRef.current([], []);
      }
    }
  }, [showSelectionColumn]);

  const handleRowSelectionToggle = useCallback((event, rowData) => {
    const rowId = getRowSelectionId(rowData);

    if (!rowId) {
      return;
    }

    const currentSelected = Array.isArray(selectedRowsRef.current)
      ? selectedRowsRef.current
      : [];
    const shouldSelect = Boolean(event?.target?.checked);
    const nextSelected = shouldSelect
      ? (currentSelected.some((row) => getRowSelectionId(row) === rowId)
        ? currentSelected
        : [...currentSelected, rowData])
      : currentSelected.filter((row) => getRowSelectionId(row) !== rowId);

    selectedRowsRef.current = nextSelected;
    setSelectedRows(nextSelected);
    if (applySelectionToTableRef.current) {
      const currentRows = Array.isArray(tableRowsRef.current)
        ? tableRowsRef.current
        : [];
      applySelectionToTableRef.current(currentRows, nextSelected);
    }
  }, [getRowSelectionId]);

  const buildRowSelectionCell = useCallback((rowData, selectedIds) => {
    const rowId = getRowSelectionId(rowData);
    const isChecked = Boolean(rowId && selectedIds.has(rowId));

    return (
      <input
        type="checkbox"
        className="po-details-row-checkbox"
        aria-label="Select row"
        data-row-id={rowId || ''}
        defaultChecked={isChecked}
        onChange={(event) => handleRowSelectionToggle(event, rowData)}
      />
    );
  }, [getRowSelectionId, handleRowSelectionToggle]);

  const applySelectionToRows = useCallback((rows, selectedIds) => (
    (Array.isArray(rows) ? rows : []).map((row) => ({
      ...row,
      [selectionColumnField]: buildRowSelectionCell(row, selectedIds),
    }))
  ), [buildRowSelectionCell]);

  const applySelectionToTable = useCallback((rows = [], nextSelectedRows = []) => {
    const selectedIds = getSelectedRowIds(nextSelectedRows);
    const rowsWithSelection = applySelectionToRows(rows, selectedIds);

    const rowIds = rows
      .map((row) => getRowSelectionId(row))
      .filter(Boolean);
    const allSelected = rowIds.length > 0 && rowIds.every((id) => selectedIds.has(id));

    tableRowsRef.current = rowsWithSelection;

    const apiCandidates = getTableApiCandidates(tableUtilityRef, tableStateRef);
    apiCandidates.forEach((tableApi) => {
      if (tableApi?.setTableData) {
        tableApi.setTableData(rowsWithSelection);
      }

      if (tableApi?.setFilteredTableData) {
        tableApi.setFilteredTableData(rowsWithSelection);
      }
    });

    if (typeof window !== 'undefined') {
      const selectAllCheckbox = document.querySelector('.po-details-select-all');
      if (selectAllCheckbox) {
        selectAllCheckbox.checked = allSelected;
        selectAllCheckbox.indeterminate = selectedIds.size > 0 && !allSelected;
      }

      const rowCheckboxes = document.querySelectorAll('.po-details-row-checkbox');
      rowCheckboxes.forEach((checkbox) => {
        const rowIdAttr = checkbox.getAttribute('data-row-id');
        const trimmedId = rowIdAttr && rowIdAttr.trim() ? rowIdAttr.trim() : null;
        const numericId = trimmedId && !Number.isNaN(Number(trimmedId))
          ? Number(trimmedId)
          : trimmedId;
        checkbox.checked = Boolean(numericId && selectedIds.has(numericId));
      });
    }

  }, [applySelectionToRows, getRowSelectionId, getSelectedRowIds]);

  useEffect(() => {
    applySelectionToTableRef.current = applySelectionToTable;
  }, [applySelectionToTable]);

  useEffect(() => {
    if (!Array.isArray(tableRowsRef.current) || tableRowsRef.current.length === 0) {
      return;
    }

    applySelectionToTable(tableRowsRef.current, selectedRows);
  }, [applySelectionToTable, selectedRows]);

  // Initialize base URL
  useEffect(() => {
    const initializeURL = async () => {
      const url = await fetchBaseURL();
      setBaseURL(url);
    };
    initializeURL();
  }, []);

  // Fetch clients for dropdown
  const fetchClients = useCallback(async () => {
    try {
      const response = await UseAxios(getClientsListRequest());

      // UseAxios returns { data: responseBody }
      const body = response && response.data ? response.data : null;

      let clientsList = [];

      // Backend returns { success: true, message: '...', data: [...] }
      if (body && body.data && Array.isArray(body.data)) {
        clientsList = body.data;
      } else if (Array.isArray(body)) {
        clientsList = body;
      } else if (body && Array.isArray(body.clients)) {
        clientsList = body.clients;
      } else if (body) {
        // Try to find any array inside the response body
        const firstArray = Object.values(body).find((v) => Array.isArray(v));
        if (Array.isArray(firstArray)) clientsList = firstArray;
      }

      if (clientsList.length === 0) {
        setClients([]);
        return [];
      }

      // Normalize client objects to the shape expected by PODetailsModal: { id, clientName }
      // Backend returns { value: client_id, label: client_name }
      const normalizedClients = clientsList
        .map((c) => ({
          id: c.value ?? c.id ?? c.client_id ?? c.clientId ?? c.clientID ?? c.client_id_value,
          clientName: c.label ?? c.clientName ?? c.client_name ?? c.client_name_text ?? c.name ?? c.client ?? '',
        }))
        .filter((c) => c.id && String(c.id).trim() !== '');

      setClients(normalizedClients);
      return normalizedClients;
    } catch (err) {
      toast.error('Failed to fetch clients: ' + (err.message || 'Network error'));
      setClients([]);
      return [];
    }
  }, []);

  // Handle Form Submission (Add/Edit)
  const handleFormSubmit = useCallback(async (data) => {
    setLoading(true);
    try {
      let response = null;

      if (editingPoId) {
        response = await UseAxios(updatePODetails(editingPoId, data));
      } else {
        response = await UseAxios(createPODetails(data));
      }

      if (response.data && response.data.success) {
        toast.success(
          response.data.message || (editingPoId ? 'Updated successfully' : 'Created successfully'),
        );
        // Close modal and reset form
        setIsEditModalOpen(false);
        setEditingPoId(null);
        set__selectedPO(null);
        setFormData({});

        // Refresh TableUtility after save.
        refreshTableUtility(tableUtilityRef, tableStateRef);
      } else {
        toast.error(response.data?.message || 'Error saving PO Details');
      }
    } catch (err) {
      toast.error(err.message || 'Error saving PO Details');
    } finally {
      setLoading(false);
    }
  }, [editingPoId]);

  // Handle Bulk Status Update
  const handleBulkStatusUpdate = useCallback(async (newStatus, comment) => {
    // Permission guard - prevent action even if modal is somehow opened
    if (!canUpdate) {
      toast.error('You do not have permission to update PO Status');
      return;
    }

    try {
      if (selectedRows.length === 0) {
        toast.warning('No rows selected');
        return;
      }

      const poIds = selectedRows
        .map((row) => resolvePoId(row))
        .filter(Boolean);

      if (poIds.length === 0) {
        toast.warning('No valid PO IDs selected');
        return;
      }

      const response = await UseAxios(bulkUpdatePOStatusRequest(poIds, newStatus, comment));

      if (response.data && response.data.success) {
        toast.success(response.data.message || 'Status updated successfully');
        setShowBulkModal(false);
        setSelectedRows([]);

        // Refresh TableUtility
        refreshTableUtility(tableUtilityRef, tableStateRef);
      } else {
        toast.error(response.data?.message || 'Error updating PO Status');
      }
    } catch (err) {
      toast.error('Error updating PO Status');
    }
  }, [selectedRows, canUpdate]);

  const handleResetTable = useCallback(() => {
    setShowBulkModal(false);
    selectedRowsRef.current = [];
    setSelectedRows([]);

    if (applySelectionToTableRef.current) {
      applySelectionToTableRef.current(tableRowsRef.current || [], []);
    }

    // Clear checkbox states explicitly to avoid stale uncontrolled UI state.
    if (typeof document !== 'undefined') {
      const rowCheckboxes = document.querySelectorAll('.po-details-row-checkbox');
      rowCheckboxes.forEach((checkbox) => {
        checkbox.checked = false;
      });

      const selectAllCheckbox = document.querySelector('.po-details-select-all');
      if (selectAllCheckbox) {
        selectAllCheckbox.checked = false;
        selectAllCheckbox.indeterminate = false;
      }
    }

    // Force a clean table mount to avoid stale checkbox UI after reset.
    setTableResetKey((prev) => prev + 1);
  }, []);

  // Handle Edit
  const handleEdit = useCallback(async (rowOrId) => {
    // Permission guard - prevent action even if button is somehow visible
    if (!canUpdate) {
      toast.error('You do not have permission to edit PO Details');
      return;
    }

    try {
      const poId = resolvePoId(rowOrId);

      if (!poId) {
        toast.error('Unable to load PO Details for editing');
        return;
      }

      const response = await UseAxios(getPODetailsById(poId));

      if (response.data && response.data.success) {
        const normalizedRow = normalizePoRow(response.data.data || {});
        setFormData(normalizedRow);
        setEditingPoId(poId);
        set__selectedPO(normalizedRow);
        setIsEditModalOpen(true);
      } else {
        toast.error('Failed to load PO Details for editing');
      }
    } catch (err) {
      toast.error('Failed to load PO Details for editing');
    }
  }, [canUpdate]);

  // Handle Delete
  const handleDelete = useCallback(async (rowOrId) => {
    // Permission guard - prevent action even if button is somehow visible
    if (!canDelete) {
      toast.error('You do not have permission to delete PO Details');
      return;
    }

    const poId = resolvePoId(rowOrId);

    if (!poId) {
      toast.error('Unable to delete PO Details');
      return;
    }

    if (window.confirm('Are you sure you want to delete this PO Details?')) {
      try {
        const response = await UseAxios(deletePODetailsRequest(poId));

        if (response.data && response.data.success) {
          toast.success(response.data.message || 'PO Details deleted successfully');

          const removedPoId = response.data.deleted_po_id || poId;
          if (removedPoId) {
            setDeletedPoIds((previousIds) => (
              previousIds.includes(removedPoId)
                ? previousIds
                : [...previousIds, removedPoId]
            ));
          }

          // Refresh TableUtility and clear selection so stale rows do not linger.
          refreshTableUtility(tableUtilityRef, tableStateRef);
          setSelectedRows([]);
        } else {
          toast.error(response?.data?.message || response?.message || 'Error deleting PO Details');
        }
      } catch (err) {
        toast.error('Error deleting PO Details');
      }
    }
  }, []);

  // Handle generic cell clicks from TableUtility. If TableUtility emits
  // `{ action: 'edit'|'delete', rowData }`, delegate to existing handlers.
  const handleCellClick = useCallback((params) => {
    const { action, rowData, value, fieldPath } = params || {};
    const actionType = action || (fieldPath === 'action' ? value : null);
    const resolvedTarget = resolvePoId(rowData)
      || resolvePoId(value)
      || resolvePoId(params?.rowId)
      || resolvePoId(params?.row)
      || resolvePoId(params?.rowKey)
      || resolvePoId(params?.id);

    if (actionType === 'edit') {
      handleEdit(resolvedTarget);

      return;
    }

    if (actionType === 'delete') {
      handleDelete(resolvedTarget);

      return;
    }

    if (fieldPath === 'po_links') {
      const poLinkUrl = getPrimaryPoLinkUrl(rowData || {}) || toNavigableUrl(value);

      if (!poLinkUrl) {
        toast.warning('PO link is not available');
        return;
      }

      window.open(poLinkUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    // Fallback: ignore clicks on action columns
    if (params?.columnKey === 'action' || params?.field === 'action') {
      return;
    }
  }, [handleEdit, handleDelete]);

  // Handle Cancel Form
  const handleCancelModal = useCallback(() => {
    setIsEditModalOpen(false);
    set__selectedPO(null);
    setEditingPoId(null);
    setFormData({});
  }, []);

  // Initial Load - Fetch Clients (only after userProfile is loaded)
  useEffect(() => {
    // Ensure userProfile is loaded before making API calls
    if (!userProfile || Object.keys(userProfile).length === 0) {
      return;
    }
    fetchClients();
  }, [fetchClients, userProfile]);

  // Handle Add New PO (triggered by table's + button) — MUST BE DEFINED BEFORE useMemo
  const handleAddNewPO = useCallback(async () => {
    // Permission guard - prevent action even if button is somehow visible
    if (!canCreate) {
      toast.error('You do not have permission to add new PO Details');
      return;
    }

    if (clients.length === 0) {
      await fetchClients();
    }
    set__selectedPO(null);
    setEditingPoId(null);
    setFormData({});
    setIsEditModalOpen(true);
  }, [clients.length, fetchClients, canCreate]);

  // TableUtility Configuration (memoized to prevent unnecessary re-renders)
  // MUST be called before any conditional returns
  const configuration = useMemo(() => ({
    generalSettings: {
      enableApiFiltering: true,
      enableFilters: true,
      baseURL,
      // Keep additional payload empty so TableUtility pagination is not overwritten.
      additionalPayloadData: {},
      userInfo: {
        emailId: userProfile.email,
        permissions: userProfile.permissions,
        roleDetails: userProfile.roleDetails,
        employeeId: userProfile.employeeId,
      },
      apiEndpoints: {
        // Backend does not provide saved 'views' for PO Details — disable these
        getAllViews: { endpoint: 'views', enabled: false },
        // Use base endpoint (GET /v1/po-details with query params)
        getFilteredData: { endpoint: '', enabled: true },
        saveOrUpdateView: { endpoint: 'view', enabled: false },
        deleteView: { endpoint: 'view-delete', enabled: false },
        download: {
          endpoint: 'export',
          enabled: true,
          filename: () => getExportFileName('PO_Details'),
          sheetName: () => getExportFileNameWithDate('PO_Details'),
        },
      },
      headers: {
        token: userProfile.access_token,
      },
      handler: {
        // Keep request handler minimal and allow TableUtility to
        // send filters in its native format so backend `parseFilterPayload` can process them.
        handleFilteredApiRequest: (requestData) => {
          if (requestData && typeof requestData === 'object') {
            delete requestData.page;
            delete requestData.limit;
            if (requestData.pagination && typeof requestData.pagination === 'object') {
              delete requestData.pagination.page;
              delete requestData.pagination.limit;
            }
          }
          return requestData;
        },
        handleFilteredApiResponse: (response) => {
          // Extract raw data before processing
          const payload = response?.data && typeof response.data === 'object' ? response.data : {};
          const tableConfiguration = Array.isArray(payload.tableConfiguration)
            ? payload.tableConfiguration
            : [];
          const tableData = (Array.isArray(payload.tableData) ? payload.tableData : [])
            .filter((rowData) => !deletedPoIds.includes(resolvePoId(rowData)));
          const responseGlobalFilters = payload.globalFilters || response?.globalFilters || [];
          const normalizedActionsColumn = getActionsColumnDefinition();
          const backendActionColumn = tableConfiguration.find((column = {}) => (
            column?.type === 'action' || column?.field === 'action'
          ));
          // Filter action buttons based on user permissions
          const finalActionColumn = backendActionColumn ? { ...backendActionColumn } : { ...normalizedActionsColumn };
          if (Array.isArray(finalActionColumn.actions)) {
            finalActionColumn.actions = finalActionColumn.actions.filter((act) => {
              const a = String(act || '').toLowerCase();
              if (a === 'edit') return canUpdate;
              if (a === 'delete') return canDelete;
              return true;
            });
          }
          const selectedIds = getSelectedRowIds(selectedRowsRef.current);

          const totalVal = Array.isArray(tableData) ? tableData.length : 0;

          const tableDataWithActions = (tableData || []).map((rowData, index) => {
            const computedSerial = totalVal > 0
              ? totalVal - index
              : index + 1;
            const poLinkUrl = getPrimaryPoLinkUrl(rowData);
            const existingRowConfiguration = rowData?.rowConfiguration || {};
            // Remove any stale po_links onClick entries — navigation is now handled
            // natively by the TableUtility type:"url" <a> renderer.
            const existingOnClickHandlers = Array.isArray(existingRowConfiguration.onClick)
              ? existingRowConfiguration.onClick.filter((item) => item && item.field !== 'po_links')
              : [];

            const formattedRowData = Object.entries(rowData || {}).reduce((acc, [fieldName, fieldValue]) => {
              if (fieldName === 'action' || fieldName === 'po_links') {
                acc[fieldName] = fieldValue;
                return acc;
              }

              const isAmountField = fieldName === 'amount' || fieldName.toLowerCase().includes('amount');
              const isCreditField = fieldName === 'credit_period' || fieldName.toLowerCase().includes('credit_period');
              const formattedValue = formatPoDisplayValue(fieldName, fieldValue);

              if (isAmountField || isCreditField) {
                const numericCellClassName = isAmountField
                  ? 'po-details-numeric-cell po-details-amount-cell'
                  : 'po-details-numeric-cell po-details-credit-cell';

                acc[fieldName] = (
                  <span className={numericCellClassName}>
                    {formattedValue}
                  </span>
                );
              } else {
                acc[fieldName] = formattedValue;
              }
              return acc;
            }, {});

            return ({
              ...formattedRowData,
              po_status: (
                <span className="po-status-plain">
                  {formattedRowData.po_status}
                </span>
              ),
              serialNumber: formattedRowData.serialNumber || formattedRowData.srNo || computedSerial,
              // Pass the validated URL string (or null) directly so the TableUtility
              // type:"url" renderer can create a real <a href target="_blank"> link.
              // Previously this was set to a JSX <span> which broke href resolution.
              po_links_raw: poLinkUrl || '',
              po_links: poLinkUrl || null,
              rowConfiguration: {
                ...existingRowConfiguration,
                onClick: existingOnClickHandlers,
              },
            });
          });

          const normalizedGlobalFilters = normalizeClientFilters(responseGlobalFilters, tableData, clients);

          const tableDataWithSelection = applySelectionToRows(tableDataWithActions, selectedIds);
          const clientIdNameMap = mergeClientMapFromMasterList(buildClientIdNameMap(tableData), clients);
          const uniqueClientNames = Array.from(new Set(
            (Array.isArray(tableData) ? tableData : [])
              .map((rowData) => getClientNameFromRow(rowData || {}))
              .filter((name) => typeof name === 'string' && name.trim()),
          ));
          const normalizedTableConfiguration = [
            ...(showSelectionColumn ? [buildSelectionColumn(tableDataWithSelection, selectedIds)] : []),
            ...tableConfiguration.filter((column) => (
              column?.type !== 'action'
              && column?.field !== 'action'
              && column?.field !== selectionColumnField
            ))
              .sort((leftColumn, rightColumn) => getPoDetailsColumnOrder(leftColumn) - getPoDetailsColumnOrder(rightColumn)),
            finalActionColumn,
          ].map((column = {}) => {
            const columnField = getPoDetailsColumnField(column);
            const headerLabel = PO_DETAILS_HEADER_LABELS[columnField];
            const isSelectionColumn = columnField === selectionColumnField;
            const shouldPinColumn = isSelectionColumn;

            const isCreditPeriod = columnField === 'credit_period'
              || String(column?.header || '')
                .toLowerCase()
                .includes('credit period');

            const isAmountField = columnField === 'amount'
              || String(column?.header || '')
                .toLowerCase()
                .includes('amount');

            const isReceivedDateField = columnField === 'received_date'
              || String(column?.header || '')
                .toLowerCase()
                .includes('received date');

            const isExpireDateField = columnField === 'expire_date'
              || String(column?.header || '')
                .toLowerCase()
                .includes('expire date');

            const normalizedMenuItems = normalizeClientFilterMenuItems(
              Array.isArray(column.menuItems) ? column.menuItems : [],
              column,
              clientIdNameMap,
              uniqueClientNames,
            );

            let finalMenuItems = normalizedMenuItems;
            const hasPoStatusFilter = Array.isArray(normalizedMenuItems) && normalizedMenuItems.some((menuItem = {}) => (
              Array.isArray(menuItem.items) && menuItem.items.some((it = {}) => it?.type === 'filter' && String(it.field || '') === 'po_status')
            ));

            if (hasPoStatusFilter) {
              // Merge and dedupe any po_status options provided by the backend across menuItems
              finalMenuItems = mergeAndDedupeFieldOptions(normalizedMenuItems, 'po_status');
            }

            // For `po_status` column: prefer backend-provided (deduped) options; if none exist,
            // derive options from the table data returned for this request (so the filter shows
            // only statuses relevant to the current dataset).
            if (columnField === 'po_status') {
              const hasOptions = Array.isArray(finalMenuItems) && finalMenuItems.some((menuItem = {}) => (
                Array.isArray(menuItem.items) && menuItem.items.some((it = {}) => it?.type === 'filter' && String(it.field || '') === 'po_status' && Array.isArray(it.options) && it.options.length > 0)
              ));

              if (!hasOptions) {
                const uniqueStatuses = Array.from(new Set(
                  (Array.isArray(tableData) ? tableData : []).map((row) => String(row?.po_status ?? row?.status ?? '').trim()).filter(Boolean),
                ));

                const derivedOptions = uniqueStatuses.map((s) => ({ label: s, value: s }));

                finalMenuItems = (finalMenuItems || []).map((menuItem = {}) => {
                  if (!Array.isArray(menuItem.items)) return menuItem;
                  const nextItems = menuItem.items.map((item = {}) => {
                    if (item?.type === 'filter' && String(item.field || '') === 'po_status') {
                      return {
                        ...item,
                        options: derivedOptions,
                        values: derivedOptions.map((o) => o.value),
                        selectedValues: Array.isArray(item.selectedValues) ? item.selectedValues.filter(Boolean) : [],
                      };
                    }
                    return item;
                  });
                  return {
                    ...menuItem,
                    items: nextItems,
                  };
                });
              }
            }

            return {
              ...column,
              ...(headerLabel ? { header: headerLabel, label: headerLabel } : {}),
              menuItems: finalMenuItems,
              ...(isAmountField
                ? {
                  className: `${column?.className || ''} po-details-amount-column`.trim(),
                  headerClassName: `${column?.headerClassName || ''} po-details-amount-column`.trim(),
                  bodyClassName: `${column?.bodyClassName || ''} po-details-amount-column`.trim(),
                }
                : {}),
              ...(isReceivedDateField
                ? {
                  className: `${column?.className || ''} po-details-received-date-column`.trim(),
                  headerClassName: `${column?.headerClassName || ''} po-details-received-date-column`.trim(),
                  bodyClassName: `${column?.bodyClassName || ''} po-details-received-date-column`.trim(),
                }
                : {}),
              ...(isExpireDateField
                ? {
                  className: `${column?.className || ''} po-details-expire-date-column`.trim(),
                  headerClassName: `${column?.headerClassName || ''} po-details-expire-date-column`.trim(),
                  bodyClassName: `${column?.bodyClassName || ''} po-details-expire-date-column`.trim(),
                }
                : {}),
              ...(isCreditPeriod
                ? {
                  className: `${column?.className || ''} po-details-credit-period-column`.trim(),
                  headerClassName: `${column?.headerClassName || ''} po-details-credit-period-column`.trim(),
                  bodyClassName: `${column?.bodyClassName || ''} po-details-credit-period-column`.trim(),
                }
                : {}),
              defaultPinned: isSelectionColumn ? column.defaultPinned : shouldPinColumn,
              isPinned: isSelectionColumn ? column.isPinned : shouldPinColumn,
              pinnable: isSelectionColumn ? column.pinnable : shouldPinColumn,
              showPinButtonInHeader: false,
              ...(isCreditPeriod || isAmountField ? { align: 'right', headerAlign: 'right' } : {}),
            };
          });

          // Keep a reference to the computed rows for DOM injection and other handlers.
          tableRowsRef.current = tableDataWithSelection;

          const metaData = {
            page: 1,
            limit: totalVal,
            total: totalVal,
            totalPages: totalVal > 0 ? 1 : 0,
          };

          // CRITICAL: Update response.data with all necessary fields
          response.data = {
            ...payload,
            tableConfiguration: normalizedTableConfiguration,
            tableData: tableDataWithSelection,
            globalFilters: normalizedGlobalFilters, // Include globalFilters in response.data
            meta: metaData, // Ensure meta is in response.data as well
          };

          // Ensure response.meta is also set correctly
          response.meta = metaData;

          // Store in state for configuration updates
          if (normalizedGlobalFilters.length > 0) {
            setGlobalFilters((previousFilters) => {
              const previousSerialized = JSON.stringify(previousFilters || []);
              const nextSerialized = JSON.stringify(normalizedGlobalFilters || []);
              return previousSerialized === nextSerialized
                ? previousFilters
                : normalizedGlobalFilters;
            });
          }

          // Normalize legacy UAT responses that still return client_id values.
          const serverApplied = normalizeAppliedFilterPayload(
            payload.appliedFilterPayload || response.appliedFilterPayload || {},
            tableData,
            clients,
          );
          response.appliedFilterPayload = serverApplied;
          response.data.appliedFilterPayload = serverApplied;
          if (serverApplied && Object.keys(serverApplied).length > 0) {
            setAppliedFilterPayload((previousPayload) => {
              const previousString = JSON.stringify(previousPayload || {});
              const nextString = JSON.stringify(serverApplied || {});
              return previousString === nextString ? previousPayload : serverApplied;
            });
          }

          return response;
        },
        onCellClickHandler: handleCellClick,
        setTableStateHandler: (stateValue) => {
          const resolvedState = typeof stateValue === 'function' ? stateValue() : stateValue;

          if (resolvedState && typeof resolvedState === 'object') {
            tableStateRef.current = resolvedState;
          }

          if (!useManualSelection && resolvedState?.selectedRows) {
            const nextSelectedRows = Array.isArray(resolvedState.selectedRows)
              ? resolvedState.selectedRows
              : [];
            setSelectedRows((prevSelectedRows) => {
              if (prevSelectedRows === nextSelectedRows) {
                return prevSelectedRows;
              }

              const sameLength = prevSelectedRows.length === nextSelectedRows.length;
              const sameItems = sameLength
                && prevSelectedRows.every((item, index) => item === nextSelectedRows[index]);

              if (sameItems) {
                return prevSelectedRows;
              }

              return nextSelectedRows;
            });
          }
        },
      },
      handlers: {
        handleAddIconClick: handleAddNewPO, // TableUtility calls this when "+" button is clicked
      },
      showToolBar: true,
      showPagination: false,
      enableColumnFilterIcon: true, // Enable filter icons in column headers
      toolBarSettings: {
        showToolBarHeading: true,
        toolBarHeading: canUpdate ? selectionHeading : titleHeading,
        showAddNew: Boolean(canCreate),
        showDownloadCsv: true,
        downloadCsvFileName: () => getExportFileName('PO_Details'),
        showColumnCustomize: false,
        showTieredMenu: true,
        showHelp: false,
        showSearch: false,
        showSaveView: false,
        showFilterOption: false,
        showFilters: false,
        showAppliedFilters: true,
        showColumnFilterMenu: false,
        showReset: true,
        onResetClick: handleResetTable,
        handleResetIconClick: handleResetTable,
        showExpandCollapse: false, // disabled: expand row button removed from toolbar
        additionalElements: [],
      },
    },
    tableSettings: {
      size: 'small',
      tableConfig: stableTableConfig,
      loaderSkeleton: {
        rows: 10,
        bodyCells: 3,
        columns: 8,
      },
      showExpandCollapse: false,
      tableScrollHeight: '500px',
      maxFrozenColumns: {
        desktop: 20,
        tablet: 20,
        mobile: 20,
      },
      paginationSettings: {
        page: 1,
        limit: 1,
        total: 0,
        totalPages: 0,
        showPageSizeDropdown: false,
        showPageSizeOptions: false,
        showPageSizeSelector: false,
        showCurrentPageReport: false,
        rowsPerPageOptions: [1],
        rows: 1,
        pageSize: 1,
      },
    },
  }), [
    baseURL,
    userProfile,
    clients,
    handleEdit,
    handleDelete,
    handleAddNewPO,
    handleResetTable,
    applySelectionToRows,
    buildSelectionColumn,
    getSelectedRowIds,
    stableTableConfig,
    selectionHeading,
    deletedPoIds,
    handleCellClick,
    canCreate,
    canUpdate,
    canDelete,
    showSelectionColumn,
  ]);

  // Ensure any injected header-level filter icon is removed (some table utilities
  // inject a global funnel button). Use a MutationObserver to remove it if re-inserted.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const selectorParts = [
      '.po-details-container .filter-icon-wrapper',
      '.po-details-container .filter-icon-btn',
      '.po-details-container .filter-icon',
      '.po-details-container i.pi.pi-filter',
    ];

    const removeFilterIcon = () => {
      const nodes = document.querySelectorAll(selectorParts.join(','));
      nodes.forEach((n) => {
        if (n && n.parentNode) n.parentNode.removeChild(n);
      });
    };

    // Initial removal
    removeFilterIcon();

    // Observe and remove if re-inserted
    const target = document.querySelector('.po-details-container') || document.body;
    const observer = new MutationObserver(removeFilterIcon);
    observer.observe(target, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  // Intercept reset icon clicks to guarantee selection reset.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const isPoDetailsRoute = () => String(window.location?.pathname || '').includes('/masters/po-details');

    const handleResetClick = (event) => {
      if (!isPoDetailsRoute()) return;

      const resetButton = event.target.closest('button');
      if (!resetButton) return;

      const clickedResetIcon
        = Boolean(event.target.closest('.pi-refresh'))
        || Boolean(resetButton.querySelector('.pi-refresh'));

      const resetHint = (
        String(resetButton.getAttribute('aria-label') || '').toLowerCase()
          .includes('reset')
        || String(resetButton.getAttribute('title') || '').toLowerCase()
          .includes('reset')
        || String(resetButton.getAttribute('data-pr-tooltip') || '').toLowerCase()
          .includes('reset')
      );

      if (!clickedResetIcon && !resetHint) return;

      event.preventDefault();
      event.stopPropagation();
      handleResetTable();
    };

    document.addEventListener('click', handleResetClick, true);
    return () => {
      document.removeEventListener('click', handleResetClick, true);
    };
  }, [handleResetTable]);

  // Keep a single checkbox column (dedupe any extra selection columns).
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const checkboxSelector = 'input[type="checkbox"], .p-checkbox, .MuiCheckbox-root';

    const tagSpecialColumnCells = () => {
      const table = document.querySelector('.po-details-container .p-datatable');
      if (!table) return;

      // Find column indices from the header so we can tag ALL body cells by index,
      // including PO Link cells that show '-' (no icon) and wouldn't be found by
      // a content-based selector.
      const headerCells = Array.from(table.querySelectorAll('thead tr th'));
      let actionColIndex = -1;
      let linkColIndex = -1;
      let creditColIndex = -1;

      headerCells.forEach((cell, idx) => {
        const titleText = String(cell.textContent || '')
          .trim()
          .toLowerCase();
        if (titleText.includes('action')) {
          cell.classList.add('po-details-actions-column');
          actionColIndex = idx;
        }
        if (titleText.includes('po link')) {
          cell.classList.add('po-details-link-column');
          linkColIndex = idx;
        }
        if (titleText.includes('credit period')) {
          cell.classList.add('po-details-credit-period-column');
          creditColIndex = idx;
        }
      });

      // Tag body cells using the recorded column indices.
      const bodyRows = Array.from(table.querySelectorAll('tbody tr'));
      bodyRows.forEach((row) => {
        const cells = Array.from(row.querySelectorAll('td'));
        cells.forEach((cell, idx) => {
          if (idx === actionColIndex || cell.querySelector('.tu-action-btn')) {
            cell.classList.add('po-details-actions-column');
          }
          if (idx === linkColIndex || cell.querySelector('.po-link-icon')) {
            cell.classList.add('po-details-link-column');
          }
          if (idx === creditColIndex) {
            cell.classList.add('po-details-credit-period-column');
          }
        });
      });
    };

    const dedupeCheckboxColumns = () => {
      const table = document.querySelector('.po-details-container .p-datatable');
      if (!table) return;

      const headerRow = table.querySelector('thead tr');
      if (!headerRow) return;

      const headerCells = Array.from(headerRow.children || []);
      const checkboxIndexes = headerCells
        .map((cell, idx) => (cell.querySelector(checkboxSelector) ? idx : -1))
        .filter((idx) => idx >= 0);

      if (checkboxIndexes.length <= 1) return;

      const removeIndexes = checkboxIndexes.slice(1).sort((a, b) => b - a);

      removeIndexes.forEach((idx) => {
        const cell = headerCells[idx];
        if (cell) cell.classList.add('po-details-hidden-column');
      });

      const bodyRows = Array.from(table.querySelectorAll('tbody tr'));
      bodyRows.forEach((row) => {
        const cells = Array.from(row.children || []);
        removeIndexes.forEach((idx) => {
          const cell = cells[idx];
          if (cell) cell.classList.add('po-details-hidden-column');
        });
      });
    };

    const removeLeadingEmptyColumn = () => {
      const table = document.querySelector('.po-details-container .p-datatable');
      if (!table) return;

      const headerRow = table.querySelector('thead tr');
      if (!headerRow) return;

      const headerCells = Array.from(headerRow.children || []);
      const firstCell = headerCells[0];
      if (!firstCell) return;

      const hasCheckbox = Boolean(firstCell.querySelector(checkboxSelector));
      const hasText = Boolean(firstCell.textContent && firstCell.textContent.trim());
      if (hasCheckbox || hasText) return;

      firstCell.classList.add('po-details-hidden-column');

      const bodyRows = Array.from(table.querySelectorAll('tbody tr'));
      bodyRows.forEach((row) => {
        const cell = row.children && row.children[0];
        if (cell) cell.classList.add('po-details-hidden-column');
      });
    };

    const hideEmptyHeaderColumns = () => {
      const table = document.querySelector('.po-details-container .p-datatable');
      if (!table) return;

      const headerRow = table.querySelector('thead tr');
      if (!headerRow) return;

      const headerCells = Array.from(headerRow.children || []);
      const emptyIndexes = headerCells
        .map((cell, idx) => {
          const hasText = Boolean(cell.textContent && cell.textContent.trim());
          if (hasText) return -1;

          const hasCheckbox = Boolean(cell.querySelector('input[type="checkbox"], .p-checkbox, .MuiCheckbox-root'));
          if (hasCheckbox) return -1;

          return idx;
        })
        .filter((idx) => idx >= 0);

      if (emptyIndexes.length === 0) return;

      emptyIndexes.forEach((idx) => {
        const cell = headerCells[idx];
        if (cell) cell.classList.add('po-details-hidden-column');
      });

      const bodyRows = Array.from(table.querySelectorAll('tbody tr'));
      bodyRows.forEach((row) => {
        const cells = Array.from(row.children || []);
        emptyIndexes.forEach((idx) => {
          const cell = cells[idx];
          if (cell) cell.classList.add('po-details-hidden-column');
        });
      });
    };

    const hideSpacerAfterCheckbox = () => {
      const table = document.querySelector('.po-details-container .p-datatable');
      if (!table) return;

      const headerRow = table.querySelector('thead tr');
      if (!headerRow) return;

      const headerCells = Array.from(headerRow.children || []);
      const checkboxIndex = headerCells.findIndex((cell) => (
        cell.querySelector('input[type="checkbox"], .p-checkbox, .MuiCheckbox-root')
      ));

      if (checkboxIndex < 0) return;

      const bodyRows = Array.from(table.querySelectorAll('tbody tr'));

      for (let idx = checkboxIndex + 1; idx < headerCells.length; idx += 1) {
        const cell = headerCells[idx];
        if (!cell) break;

        const hasText = Boolean(cell.textContent && cell.textContent.trim());
        const hasControl = Boolean(cell.querySelector('input, button, svg, .pi'));
        if (hasText || hasControl) break;

        cell.classList.add('po-details-hidden-column');

        bodyRows.forEach((row) => {
          const bodyCell = row.children && row.children[idx];
          if (bodyCell) bodyCell.classList.add('po-details-hidden-column');
        });
      }
    };

    const hideNarrowEmptyFrozenColumns = () => {
      const table = document.querySelector('.po-details-container .p-datatable');
      if (!table) return;

      const headerRow = table.querySelector('thead tr');
      if (!headerRow) return;

      const headerCells = Array.from(headerRow.children || []);
      const bodyRows = Array.from(table.querySelectorAll('tbody tr'));

      headerCells.forEach((cell, idx) => {
        if (!cell) return;

        const hasText = Boolean(cell.textContent && cell.textContent.trim());
        if (hasText) return;

        const hasCheckbox = Boolean(cell.querySelector('input[type="checkbox"], .p-checkbox, .MuiCheckbox-root'));
        if (hasCheckbox) return;

        const width = cell.getBoundingClientRect().width;
        if (width > 100) return;

        cell.classList.add('po-details-hidden-column');
        bodyRows.forEach((row) => {
          const bodyCell = row.children && row.children[idx];
          if (bodyCell) bodyCell.classList.add('po-details-hidden-column');
        });
      });
    };

    tagSpecialColumnCells();
    dedupeCheckboxColumns();
    removeLeadingEmptyColumn();
    hideEmptyHeaderColumns();
    hideSpacerAfterCheckbox();
    hideNarrowEmptyFrozenColumns();

    const target = document.querySelector('.po-details-container') || document.body;
    const observer = new MutationObserver(() => {
      tagSpecialColumnCells();
      dedupeCheckboxColumns();
      removeLeadingEmptyColumn();
      hideEmptyHeaderColumns();
      hideSpacerAfterCheckbox();
      hideNarrowEmptyFrozenColumns();
    });
    observer.observe(target, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  // Check for early return after ALL hooks are defined
  const isUserProfileReady = userProfile && typeof userProfile === 'object' && userProfile.email;
  // Short helper flags and strings for JSX to avoid very long lines (eslint max-len)
  if (!baseURL || !isUserProfileReady) {
    return <Loader />;
  }

  return (
    <div className="po-details-container">
      {/* TableUtility Component */}
      <div className="po-details-table-wrapper">
        <TableUtility
          key={tableResetKey}
          ref={tableUtilityRef}
          configuration={configuration}
        />
      </div>

      {/* Modal for Add/Edit PO */}
      <PODetailsModal
        open={isEditModalOpen}
        isEdit={Boolean(editingPoId)}
        initialData={formData}
        clients={clients}
        onSubmit={handleFormSubmit}
        onCancel={handleCancelModal}
        isLoading={loading}
      />

      {/* Bulk Status Update Modal */}
      {showBulkModal && (
        <BulkStatusModal
          selectedRows={selectedRows}
          statuses={poStatuses}
          onUpdate={handleBulkStatusUpdate}
          onCancel={() => setShowBulkModal(false)}
          isLoading={false}
        />
      )}
    </div>
  );
};

PoDetails.propTypes = {
  location: PropTypes.shape({
    search: PropTypes.string,
  }),
};

export default PoDetails;
